// 從「依日期分組」的場次資料中，找出現在時間點最相關的場次（進行中優先排在最前，其餘依時間排序）
// days: [{ year, month, date, ...items }]
// getItems(day) 取出該天的場次/項目陣列；getTimeRange(day, item) 回傳 { start: Date, end: Date }
//
const UPCOMING_WINDOW_MS = 30 * 60 * 1000

// 狀態規則：
// - 現在落在區間內 → 'ongoing'
// - 未來場次中，時間上最接近現在、且開始時間在 30 分鐘內的那一場 → 'upcoming'
// - 其餘更晚（或最近一場但還沒進入 30 分鐘窗口）的未來場次 → null（不掛狀態標籤）
// - 已結束的場次 → 'ended'（當日資訊仍保留顯示，只是不掛狀態標籤）
function withStatus(now, entries) {
  const nearestUpcomingStart = entries
    .filter(({ start }) => start > now)
    .reduce((min, { start }) => (min == null || start < min ? start : min), null)

  return entries.map(({ item, start, end }) => {
    let status
    if (now >= start && now <= end) status = 'ongoing'
    else if (start > now) {
      const isNearest = start.getTime() === nearestUpcomingStart?.getTime()
      status = isNearest && start.getTime() - now.getTime() <= UPCOMING_WINDOW_MS ? 'upcoming' : null
    } else status = 'ended'
    return { ...item, status, start, end }
  })
}

// 依「今天（本地日期）」鎖定單一天，不跨天。
// 若今天不在活動日期範圍內（尚未開始或已結束），改用最接近的那一天（未開始取第一天，已結束取最後一天）。
export function findTargetDay(days, now = new Date()) {
  if (days.length === 0) return null

  const exactToday = days.find(
    (day) => day.year === now.getFullYear() && Number(day.month) === now.getMonth() + 1 && Number(day.date) === now.getDate()
  )
  if (exactToday) return exactToday

  const sorted = [...days].sort((a, b) => new Date(a.year, a.month - 1, a.date) - new Date(b.year, b.month - 1, b.date))
  const firstDayStart = new Date(sorted[0].year, sorted[0].month - 1, sorted[0].date)
  return now < firstDayStart ? sorted[0] : sorted[sorted.length - 1]
}

function collectWithRange(days, getItems, getTimeRange) {
  const now = new Date()
  const targetDay = findTargetDay(days, now)
  if (!targetDay) return []

  const all = getItems(targetDay).map((item) => ({ item, ...getTimeRange(targetDay, item) }))
  const withStatuses = withStatus(now, all)

  // 當日全部場次依時間順序顯示（已結束的場次仍保留，不重新排序、不濾除）；不跨到其他天
  return withStatuses.sort((a, b) => a.start - b.start)
}

export function findUpcomingList(days, getItems, getTimeRange, limit = 4) {
  return collectWithRange(days, getItems, getTimeRange).slice(0, limit)
}

function parseTimeRangeString(day, timeRange) {
  const [startStr, endStr] = timeRange.split('-')
  const [sh, sm] = startStr.split(':').map(Number)
  const [eh, em] = endStr.split(':').map(Number)
  return {
    start: new Date(day.year, day.month - 1, day.date, sh, sm),
    end: new Date(day.year, day.month - 1, day.date, eh, em),
  }
}

// 講座／市集：sessions: [{ time: 'HH:MM-HH:MM', ... }]
export function findUpcomingSessions(days, limit = 4) {
  return findUpcomingList(days, (day) => day.sessions, (day, session) => parseTimeRangeString(day, session.time), limit)
}

// 挑戰賽：entries: [{ type: 'match', matchTime: 'HH:MM', departureTime: 'HH:MM', ... } | { type: 'break', time: 'HH:MM-HH:MM', ... }]
export function findUpcomingChallengeEntries(days, limit = 4) {
  return findUpcomingList(
    days,
    (day) => day.entries,
    (day, entry) => {
      if (entry.type === 'break') return parseTimeRangeString(day, entry.time)
      return parseTimeRangeString(day, `${entry.matchTime}-${entry.departureTime}`)
    },
    limit
  )
}

// 挑戰賽：只取「今天（本地日期）」的比賽場次，排除休息時間，依時間排序。
// 若今天不在活動日期範圍內（尚未開始或已結束），改用最接近的那一天（未開始取第一天，已結束取最後一天）。
// 挑戰賽不套用進行中／即將開始的狀態判斷，單純依時間順序列出。
export function findTodayChallengeMatches(days) {
  const targetDay = findTargetDay(days)
  if (!targetDay) return []

  const matches = targetDay.entries
    .filter((entry) => entry.type !== 'break')
    .map((entry) => ({ ...entry, ...parseTimeRangeString(targetDay, `${entry.matchTime}-${entry.departureTime}`) }))

  return matches.sort((a, b) => a.start - b.start)
}
